import React, { useState, useEffect } from "react";
import { ArrowUpDown, CheckCircle, AlertCircle } from "lucide-react";
import { findPhotoForStudent } from "../../utils/photoUtils";
import "../../styles/FourUpSortModal.css";

const FourUpSortModal = ({
  isOpen,
  onClose,
  onContinue,
  students,
  filteredStudents = [],
  photos = [],
}) => {
  // Get available fields from students data
  const getAvailableFields = () => {
    if (!students || students.length === 0) return [];

    const allFields = new Set();
    students.forEach((student) => {
      Object.keys(student).forEach((key) => {
        if (!["id", "createdAt", "updatedAt"].includes(key)) {
          allFields.add(key);
        }
      });
    });

    // Common sorting fields in preferred order
    const preferredOrder = [
      "Last Name",
      "First Name",
      "Grade",
      "Teacher",
      "Homeroom",
      "Student ID",
      "Subject ID",
      "SASID",
      "Student Number",
    ];

    const availableFields = [];

    // Add preferred fields first if they exist
    preferredOrder.forEach((field) => {
      if (allFields.has(field)) {
        availableFields.push(field);
        allFields.delete(field);
      }
    });

    // Add remaining fields alphabetically
    const remainingFields = Array.from(allFields).sort();
    availableFields.push(...remainingFields);

    return availableFields;
  };

  const availableFields = getAvailableFields();

  // Default sort order
  const [sortOrder, setSortOrder] = useState([
    "Last Name",
    "First Name",
    "Grade",
    "Teacher",
  ]);

  // Data source selection - will be updated after photo calculation
  const [useFilteredData, setUseFilteredData] = useState(true);

  // Page break selection
  const [pageBreakField, setPageBreakField] = useState("none");

  // Track active preset
  const [activePreset, setActivePreset] = useState(null);

  // Calculate counts of students WITH PHOTOS - moved up before useEffect
  const allStudentsWithPhotos = students?.filter(student => 
    findPhotoForStudent(student, photos)
  ) || [];
  const filteredStudentsWithPhotos = filteredStudents?.filter(student => 
    findPhotoForStudent(student, photos)
  ) || [];
  
  const allStudentsWithPhotosCount = allStudentsWithPhotos.length;
  const filteredStudentsWithPhotosCount = filteredStudentsWithPhotos.length;
  
  // Check if we have any students with photos
  const hasAnyPhotos = allStudentsWithPhotosCount > 0 || filteredStudentsWithPhotosCount > 0;
  
  // Determine which data sources are available
  const canUseFiltered = filteredStudentsWithPhotosCount > 0;
  const canUseAll = allStudentsWithPhotosCount > 0;
  
  // Check if filtering is meaningful (both have photos and counts are different)
  const hasValidFilter = canUseFiltered && canUseAll && 
    filteredStudentsWithPhotosCount < allStudentsWithPhotosCount;

  // Auto-select appropriate data source when modal opens
  useEffect(() => {
    if (isOpen) {
      if (canUseFiltered && !canUseAll) {
        setUseFilteredData(true);
      } else if (!canUseFiltered && canUseAll) {
        setUseFilteredData(false);
      }
      // Otherwise keep the current selection
    }
  }, [isOpen, canUseFiltered, canUseAll]);

  if (!isOpen) return null;

  const handleFieldChange = (index, value) => {
    const newOrder = [...sortOrder];
    newOrder[index] = value;
    setSortOrder(newOrder);
    // Clear active preset when manually changing sort
    setActivePreset(null);
  };

  // Handle preset selections
  const handlePresetGrade = () => {
    setSortOrder(["Grade", "Last Name", "First Name", ""]);
    setPageBreakField("Grade");
    setActivePreset("grade");
  };

  const handlePresetHomeroom = () => {
    setSortOrder(["Homeroom", "Last Name", "First Name", ""]);
    setPageBreakField("Homeroom");
    setActivePreset("homeroom");
  };

  const handlePresetAlphabetical = () => {
    setSortOrder(["Last Name", "First Name", "Grade", "Teacher"]);
    setPageBreakField("none");
    setActivePreset("alphabetical");
  };

  const handleContinue = () => {
    // Determine which data source to use based on availability
    let dataSource;
    if (hasValidFilter) {
      // Both sources available, use user selection
      dataSource = useFilteredData;
    } else if (canUseFiltered && !canUseAll) {
      // Only filtered has photos
      dataSource = true;
    } else {
      // Only all has photos or neither (shouldn't happen)
      dataSource = false;
    }
    onContinue(sortOrder, dataSource, pageBreakField);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-container modal-container-compact">
        <div className="modal-header modal-header-compact">
          <div className="modal-icon-container modal-info-icon">
            <ArrowUpDown style={{ width: "20px", height: "20px" }} />
          </div>
          <div>
            <h2 className="modal-title">4-Up Export Sort Order</h2>
          </div>
        </div>

        <div className="modal-body modal-body-compact">
          {!hasAnyPhotos ? (
            <div className="no-photos-warning">
              <div className="warning-icon">
                <AlertCircle style={{ width: "24px", height: "24px", color: "#d97706" }} />
              </div>
              <h3 className="warning-title">No Students with Photos</h3>
              <p className="warning-text">
                Cannot create 4-Up labels without student photos.
              </p>
              <ul className="warning-list">
                <li>Upload photos for students first</li>
                <li>Photos must match student names exactly</li>
              </ul>
            </div>
          ) : (
            <>
              <div className="modal-content modal-content-compact">
                Choose the sort order for your 4-Up labels and select which students
                to include.
              </div>

          {hasValidFilter ? (
            <div className="data-source-selection data-source-selection-compact">
              <h3 className="data-source-title">Students to Export</h3>

              <div className="data-source-options">
                <label className="data-source-option data-source-option-compact">
                  <input
                    type="radio"
                    name="dataSource"
                    checked={useFilteredData}
                    onChange={() => setUseFilteredData(true)}
                    className="data-source-radio"
                  />
                  <span className="data-source-label">
                    <strong>
                      Filtered Students Only ({filteredStudentsWithPhotosCount} with photos)
                    </strong>
                    <br />
                    <small>
                      Export only students currently visible in the Students view who have photos
                    </small>
                  </span>
                </label>

                <label className="data-source-option data-source-option-compact">
                  <input
                    type="radio"
                    name="dataSource"
                    checked={!useFilteredData}
                    onChange={() => setUseFilteredData(false)}
                    className="data-source-radio"
                  />
                  <span className="data-source-label">
                    <strong>All Students ({allStudentsWithPhotosCount} with photos)</strong>
                    <br />
                    <small>
                      Export all students in the school who have photos (ignores current filters)
                    </small>
                  </span>
                </label>
              </div>
            </div>
          ) : (
            <div className="export-info">
              <h3 className="export-info-title">Students to Export</h3>
              <p className="export-info-text">
                {canUseFiltered && !canUseAll 
                  ? `Exporting ${filteredStudentsWithPhotosCount} filtered students with photos`
                  : `Exporting all ${allStudentsWithPhotosCount} students with photos in the school`}
                {filteredStudents.length > 0 && filteredStudentsWithPhotosCount === 0 && 
                  " (filtered students have no photos)"}
              </p>
            </div>
          )}

          {/* Preset Buttons */}
          <div className="preset-buttons-container" style={{ marginBottom: '20px' }}>
            <h3 className="preset-buttons-title" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>
              Quick Presets
            </h3>
            <div className="preset-buttons-row" style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <button
                onClick={handlePresetGrade}
                className={`preset-button ${activePreset === 'grade' ? 'preset-button-active' : ''}`}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  background: activePreset === 'grade' ? '#3b82f6' : '#fff',
                  color: activePreset === 'grade' ? '#fff' : '#374151',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                Group by Grade
              </button>
              <button
                onClick={handlePresetHomeroom}
                className={`preset-button ${activePreset === 'homeroom' ? 'preset-button-active' : ''}`}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  background: activePreset === 'homeroom' ? '#3b82f6' : '#fff',
                  color: activePreset === 'homeroom' ? '#fff' : '#374151',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                Group by Homeroom
              </button>
              <button
                onClick={handlePresetAlphabetical}
                className={`preset-button ${activePreset === 'alphabetical' ? 'preset-button-active' : ''}`}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  background: activePreset === 'alphabetical' ? '#3b82f6' : '#fff',
                  color: activePreset === 'alphabetical' ? '#fff' : '#374151',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                Alphabetical
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0' }}>
              Select a preset to automatically set sort order and page breaks, or customize below
            </p>
          </div>

          <div className="sort-fields-container sort-fields-container-compact">
            <h3 className="sort-fields-title">Sort Priority</h3>

            <div className="sort-fields-grid">
              {[0, 1, 2, 3].map((index) => (
                <div
                  key={index}
                  className="sort-field-row sort-field-row-compact"
                >
                  <div className="sort-field-number sort-field-number-compact">
                    {index + 1}
                  </div>
                  <select
                    value={sortOrder[index] || ""}
                    onChange={(e) => handleFieldChange(index, e.target.value)}
                    className="sort-field-select sort-field-select-compact"
                  >
                    <option value="">-- Select Field --</option>
                    {availableFields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="sort-preview sort-preview-compact">
            <div className="sort-preview-text">
              <strong>Sort:</strong>{" "}
              {sortOrder.filter(Boolean).length > 0 ? (
                sortOrder.filter(Boolean).join(" → ")
              ) : (
                <em>Select at least one field</em>
              )}
            </div>
          </div>

          {/* Page Break Selection */}
          <div className="page-break-container">
            <h3 className="page-break-title">Page Breaks</h3>
            <div className="page-break-content">
              <label className="page-break-label">
                Start new page when this field changes:
              </label>
              <select
                value={pageBreakField}
                onChange={(e) => {
                  setPageBreakField(e.target.value);
                  setActivePreset(null);  // Clear preset when manually changing page break
                }}
                className="page-break-select"
              >
                <option value="none">No page breaks (continuous)</option>
                {sortOrder.filter(Boolean).map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
              {pageBreakField !== "none" && (
                <small className="page-break-help">
                  A new page will start each time the {pageBreakField} value changes
                </small>
              )}
            </div>
          </div>
            </>
          )}
        </div>

        <div className="modal-footer modal-footer-compact">
          <button
            onClick={onClose}
            className="modal-button modal-button-secondary"
          >
            {hasAnyPhotos ? "Cancel" : "Close"}
          </button>
          {hasAnyPhotos && (
            <button
              onClick={handleContinue}
              className="modal-button modal-button-primary"
              disabled={sortOrder.filter(Boolean).length === 0}
            >
              <CheckCircle style={{ width: "16px", height: "16px" }} />
              Create 4-Up PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FourUpSortModal;
